/**
 * Placeholder for a value the data genuinely does not have, as opposed to a
 * value that is really zero. Rendering `0.00` for missing data was the original
 * complaint about this page, so every formatter here returns this instead.
 */
export const NO_VALUE = '—';

export const formatNumber = (
  value: number | null | undefined,
  decimals: number
): string =>
  value === null || value === undefined || Number.isNaN(value)
    ? NO_VALUE
    : value.toFixed(decimals);

export const formatInteger = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value)
    ? NO_VALUE
    : Math.round(value).toString();

export const formatPrice = (price: number | null | undefined): string =>
  price === null || price === undefined ? NO_VALUE : `£${price.toFixed(1)}m`;

export const formatOwnership = (ownership: number | null | undefined): string =>
  ownership === null || ownership === undefined
    ? NO_VALUE
    : `${ownership.toFixed(1)}%`;

export const formatPercentage = (fraction: number | null | undefined): string =>
  fraction === null || fraction === undefined || Number.isNaN(fraction)
    ? NO_VALUE
    : `${Math.round(fraction * 100)}%`;

const ORDINAL_SUFFIXES = ['th', 'st', 'nd', 'rd'] as const;
const SPECIAL_CASE_START = 11;
const SPECIAL_CASE_END = 13;

/** Renders a percentile as an ordinal, e.g. 74 to "74th". */
export const formatOrdinal = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return NO_VALUE;

  const rounded = Math.round(value);
  const lastTwoDigits = rounded % 100;
  const lastDigit = rounded % 10;

  const suffix =
    lastTwoDigits >= SPECIAL_CASE_START && lastTwoDigits <= SPECIAL_CASE_END
      ? ORDINAL_SUFFIXES[0]
      : ORDINAL_SUFFIXES[lastDigit] ?? ORDINAL_SUFFIXES[0];

  return `${rounded}${suffix}`;
};

/** Signed delta for the over/underperformance pills, e.g. "+1.42" or "-0.30". */
export const formatDelta = (value: number, decimals: number): string =>
  `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(decimals)}`;

const AVAILABILITY_LABEL: Record<string, string> = {
  a: 'Fit',
  d: 'Doubtful',
  i: 'Injured',
  s: 'Suspended',
  n: 'Ineligible',
  u: 'Unavailable'
};

/**
 * The design asked for a "92% start odds" pill, but no start-probability field
 * exists. This renders what the API actually knows: the FPL availability flag,
 * qualified by chance of playing when the player is flagged.
 */
export const describeAvailability = (
  status: string | null | undefined,
  chanceOfPlaying: number | null | undefined
): { label: string; isAvailable: boolean } => {
  const label = AVAILABILITY_LABEL[status ?? ''] ?? 'Unknown';
  const isAvailable = status === 'a';

  if (
    isAvailable ||
    chanceOfPlaying === null ||
    chanceOfPlaying === undefined
  ) {
    return { label, isAvailable };
  }

  return { label: `${label} · ${chanceOfPlaying}% chance`, isAvailable };
};
